import React, { useEffect, useRef, useState } from 'react';

interface DynamicGoogleMapProps {
  businessName: string;
  fallbackAddress?: string;
  className?: string;
  height?: string;
}

declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const DynamicGoogleMap: React.FC<DynamicGoogleMapProps> = ({
  businessName,
  fallbackAddress,
  className,
  height,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps || !window.google.maps.Map) {
      console.log('Google Maps API not fully loaded yet');
      return;
    }

    try {
      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 15,
        center: { lat: 29.9356986, lng: 78.0745323 }, // Haridwar location
        styles: [
          {
            "featureType": "all",
            "elementType": "geometry.fill",
            "stylers": [{ "weight": "2.00" }]
          },
          {
            "featureType": "all",
            "elementType": "geometry.stroke",
            "stylers": [{ "color": "#9c9c9c" }]
          },
          {
            "featureType": "all",
            "elementType": "labels.text",
            "stylers": [{ "visibility": "on" }]
          },
          {
            "featureType": "landscape",
            "elementType": "all",
            "stylers": [{ "color": "#f2f2f2" }]
          },
          {
            "featureType": "landscape",
            "elementType": "geometry.fill",
            "stylers": [{ "color": "#ffffff" }]
          },
          {
            "featureType": "landscape.man_made",
            "elementType": "geometry.fill",
            "stylers": [{ "color": "#ffffff" }]
          },
          {
            "featureType": "poi",
            "elementType": "all",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "road",
            "elementType": "all",
            "stylers": [{ "saturation": -100 }, { "lightness": 45 }]
          },
          {
            "featureType": "road",
            "elementType": "geometry.fill",
            "stylers": [{ "color": "#eeeeee" }]
          },
          {
            "featureType": "road",
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#7b7b7b" }]
          },
          {
            "featureType": "road",
            "elementType": "labels.text.stroke",
            "stylers": [{ "color": "#ffffff" }]
          },
          {
            "featureType": "road.highway",
            "elementType": "all",
            "stylers": [{ "visibility": "simplified" }]
          },
          {
            "featureType": "road.arterial",
            "elementType": "labels.icon",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "transit",
            "elementType": "all",
            "stylers": [{ "visibility": "off" }]
          },
          {
            "featureType": "water",
            "elementType": "all",
            "stylers": [{ "color": "#46bcec" }, { "visibility": "on" }]
          },
          {
            "featureType": "water",
            "elementType": "geometry.fill",
            "stylers": [{ "color": "#c8d7d4" }]
          },
          {
            "featureType": "water",
            "elementType": "labels.text.fill",
            "stylers": [{ "color": "#070707" }]
          },
          {
            "featureType": "water",
            "elementType": "labels.text.stroke",
            "stylers": [{ "color": "#ffffff" }]
          }
        ]
      });

      // Use geocoding instead of deprecated Places API
      const geocoder = new window.google.maps.Geocoder();
      
      // Try to geocode the business name first
      geocoder.geocode({ address: `${businessName}, Shivalik Nagar, Haridwar, India` }, (results: any, status: any) => {
      setIsLoading(false);
      
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        
        // Center map on the found location
        map.setCenter(location);
        map.setZoom(16);
        
        // Create marker for the location
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          new window.google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: location,
            title: businessName,
            content: new DOMParser().parseFromString(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="#ffffff" stroke-width="2"/>
                <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                <circle cx="20" cy="20" r="4" fill="#3B82F6"/>
              </svg>
            `, 'image/svg+xml').documentElement
          });
        } else {
          new window.google.maps.Marker({
            position: location,
            map: map,
            title: businessName,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="#ffffff" stroke-width="2"/>
                  <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                  <circle cx="20" cy="20" r="4" fill="#3B82F6"/>
                </svg>
              `)}`,
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20)
            }
          });
        }

        // Remove info window functionality - using persistent card instead

      } else {
        // Fallback to default location if geocoding fails
        const fallbackLocation = { lat: 29.9356986, lng: 78.0745323 };
        map.setCenter(fallbackLocation);
        map.setZoom(15);
        
        // Create fallback marker for approximate location
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          new window.google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: fallbackLocation,
            title: businessName,
            content: new DOMParser().parseFromString(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#ef4444" stroke="#ffffff" stroke-width="2"/>
                <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                <circle cx="20" cy="20" r="4" fill="#ef4444"/>
              </svg>
            `, 'image/svg+xml').documentElement
          });
        } else {
          new window.google.maps.Marker({
            position: fallbackLocation,
            map: map,
            title: businessName,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="18" fill="#ef4444" stroke="#ffffff" stroke-width="2"/>
                  <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                  <circle cx="20" cy="20" r="4" fill="#ef4444"/>
                </svg>
              `)}`,
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20)
            }
          });
        }
        // Remove fallback info window - using persistent card instead
      }
      });
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
      setError('Failed to initialize Google Maps');
      setIsLoading(false);
    }
  };

  const loadGoogleMapsScript = () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      setError('Google Maps API key not configured');
      setIsLoading(false);
      return;
    }

    // Check if Google Maps is already fully loaded
    if (window.google && window.google.maps && window.google.maps.Map) {
      console.log('Google Maps already loaded, initializing...');
      initializeMap();
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('Google Maps script already exists, waiting for load...');
      // Script is already loading, wait for it with timeout
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds timeout
      const checkGoogle = setInterval(() => {
        attempts++;
        if (window.google && window.google.maps && window.google.maps.Map) {
          console.log('Google Maps loaded after waiting');
          clearInterval(checkGoogle);
          initializeMap();
        } else if (attempts >= maxAttempts) {
          console.error('Timeout waiting for Google Maps to load');
          clearInterval(checkGoogle);
          setError('Timeout loading Google Maps');
          setIsLoading(false);
        }
      }, 100);
      return;
    }

    console.log('Loading Google Maps script...');
    
    // Create unique callback name to avoid conflicts
    const callbackName = `initMap_${Date.now()}`;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    
    // Set up the callback
    (window as any)[callbackName] = () => {
      console.log('Google Maps callback fired');
      // Clean up the callback
      delete (window as any)[callbackName];
      
      // Double-check that everything is loaded
      if (window.google && window.google.maps && window.google.maps.Map) {
        console.log('Google Maps fully loaded, initializing map...');
        initializeMap();
      } else {
        console.error('Google Maps callback fired but API not available');
        setError('Google Maps API not properly loaded');
        setIsLoading(false);
      }
    };
    
    script.onerror = (error) => {
      console.error('Failed to load Google Maps script:', error);
      setError('Failed to load Google Maps script');
      setIsLoading(false);
      // Clean up the callback
      delete (window as any)[callbackName];
    };
    
    document.head.appendChild(script);
  };

  useEffect(() => {
    loadGoogleMapsScript();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="relative w-full h-full">
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center">
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className || 'w-full h-full'}`} style={height ? { height } : undefined}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading map...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Persistent Info Card - Bottom Left */}
      <div className="absolute bottom-4 left-4 z-20 max-w-sm">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden backdrop-blur-sm bg-white/95">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                <span className="text-white font-bold text-lg">SS</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg leading-tight">{businessName}</h3>
                <p className="text-blue-100 text-xs">Solar Energy Solutions</p>
              </div>
            </div>
          </div>
          
          {/* Address Section */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-start">
              <div className="text-blue-600 mr-2 mt-0.5">
                📍
              </div>
              <div className="flex-1">
                <p className="text-gray-800 text-sm font-medium leading-relaxed">
                  C-97, Opposite ABCD Park<br />
                  Shivalik Nagar<br />
                  Haridwar (U.K.) - 249403
                </p>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex gap-2">
              <a 
                href="https://www.google.com/maps/dir//29.9356157,78.0746983/@29.9342278,78.0752565,16.75z?entry=ttu&g_ep=EgoyMDI1MDgxOS4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xs font-semibold py-2.5 px-3 rounded-lg transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
              >
                <span className="mr-1.5">🧭</span>
                Direction
              </a>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('SS Solar, Shivalik Nagar, Haridwar')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-semibold py-2.5 px-3 rounded-lg transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
              >
                <span className="mr-1.5">🗺️</span>
                Map
              </a>
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="px-4 py-2.5 bg-gray-50">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span className="flex items-center">
                <span className="mr-1">📞</span>
                +91 98877 10260
              </span>
              <span className="flex items-center">
                <span className="mr-1">🌐</span>
                sssolarenergy.in
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicGoogleMap;
